import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import CCBillModal from "../../Pages/CCbill/CCBillModal";
import apiCache from "../../../shared/utils/apiCache";
import {
  showPaymentProcessing,
  showPaymentCancelling,
  closeAlert,
  confirmDialog,
} from "../../../shared/utils/swalHandler";
import {
  showPurchaseFailedPushToast,
  showContentUnlockedPushToast,
} from "../../../shared/pusNotification/useManualToasts";
import {
  purchaseContent,
  purchaseMessageContent,
  cancelPurchase,
  cancelMessagePurchase,
} from "../../../shared/actions/purchaseAction";
import { charge } from "../../../shared/actions/ccbillAction";
import "./style.css";
import { PAYMENT_INFO, PURCHASE_STATE } from "../../../shared/constants/common";
import {
  hasValidValue,
  getFormFieldValue,
  buildPassThroughInfo,
  extractPurchaseDetails,
  buildPurchasePayload,
  getVatAndServiceFeeAppliedText,
} from "../../../shared/utils/paymentUtil";
import { useModalOpen } from "../../../shared/utils/modalUtils.js";
import { setPaymentFlowActive } from "../../../shared/utils/config/paymentFlowGuard";
import { formatAmountWithTwoDecimalPlaces } from "../../../shared/utils/function";

/**
 * True when the purchase API response indicates payment already completed (not merely "one-click eligible").
 * DM / message PPV must not unlock on `paymentOneClick` alone — that flag can be set while a charge is still required.
 */
const PURCHASE_PAYMENT_FAILED_TITLE = "Payment Failed";
const PAYMENT_PROCESSING_MIN_DISPLAY_MS = 3000;

const waitForPaymentSuccessLoader = (minMs = PAYMENT_PROCESSING_MIN_DISPLAY_MS) =>
  new Promise((resolve) => setTimeout(resolve, minMs));

const isPurchaseApiConfirmedComplete = (responseData) => {
  if (!responseData || typeof responseData !== "object") return false;
  const nestedState = responseData.purchase?.state;
  const topState = responseData.state;
  const state = nestedState !== undefined && nestedState !== null ? nestedState : topState;
  if (Number(state) === PURCHASE_STATE.ACTIVE) return true;
  if (responseData.isPurchased === true) return true;
  if (responseData.isPurchasedOrSubscribed === true) return true;
  return false;
};

const PurchaseContentModal = ({
  isOpen = false,
  toggle = null,
  contentId = null,
  contentPrice = 0,
  creatorInfo = null,
  post = null,
  onPurchaseSuccess = null,
  user = null,
  purchase_type = 'content',
}) => {
  const [ccBillModalOpen, setCcBillModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null);
  const [purchaseTransactionId, setPurchaseTransactionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBrowser, setIsBrowser] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hasSavedPaymentMethod, setHasSavedPaymentMethod] = useState(false);

  const isOpenRef = useRef(isOpen);
  const paymentProcessingStartedAtRef = useRef(null);
  const [purchasePayload, setPurchasePayload] = useState(null);

  const openPaymentProcessing = useCallback((options = {}) => {
    paymentProcessingStartedAtRef.current = Date.now();
    return showPaymentProcessing(options);
  }, []);

  const dismissPaymentProcessing = useCallback(() => {
    paymentProcessingStartedAtRef.current = null;
    closeAlert();
  }, []);

  const showPurchasePaymentError = useCallback(
    (message) => {
      showPurchaseFailedPushToast(
        PURCHASE_PAYMENT_FAILED_TITLE,
        message || "Payment failed. Please try again.",
        purchase_type
      );
    },
    [purchase_type]
  );

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(isOpen || showConfirmationModal);

  // Block signed-URL refresh (page reload) while purchase payment is in progress
  useEffect(() => {
    const active = isOpen || showConfirmationModal || ccBillModalOpen;
    setPaymentFlowActive(active);
    return () => setPaymentFlowActive(false);
  }, [isOpen, showConfirmationModal, ccBillModalOpen]);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  useEffect(() => {
    // if (isOpen) {
    const fetchPurchasePayload = async () => {
      const payload = await buildPurchasePayload(contentPrice, post?.real_price, user);
      setPurchasePayload(payload);
    };
    fetchPurchasePayload();
    // }
  }, [contentPrice, post?.real_price, user, isOpen]);

  const purchaseSummary = useMemo(() => {
    const username = creatorInfo?.username || "";
    const vatAmount = purchasePayload?.vatAmount ?? 0;
    const totalAmount = purchasePayload?.amount ?? 0;

    return {
      username,
      vatDisplay: formatAmountWithTwoDecimalPlaces(vatAmount),
      totalDisplay: formatAmountWithTwoDecimalPlaces(totalAmount),
    };
  }, [purchasePayload, creatorInfo]);

  const resetPurchaseState = useCallback(() => {
    setPaymentForm(null);
    setPurchaseTransactionId(null);
    setIsProcessing(false);
  }, []);

  const fetchSavedPaymentToken = useCallback(async () => {
    const token = await apiCache.getPaymentTokenId();
    return hasValidValue(token) ? token : null;
  }, []);

  const cancelPurchaseByType = useCallback(
    async (purchaseId, isManualRemove = false) => {
      if (!purchaseId) return;
      if (purchase_type === "message_content") {
        await cancelMessagePurchase(purchaseId, isManualRemove);
      } else {
        await cancelPurchase(purchaseId, isManualRemove);
      }
    },
    [purchase_type]
  );

  const closeModal = useCallback(() => {
    setCcBillModalOpen(false);
    setShowConfirmationModal(false);
    resetPurchaseState();
    closeAlert();
    // Ensure scroll is restored when closing modal
    if (toggle) {
      toggle();
    }
  }, [resetPurchaseState, toggle]);

  const handlePurchaseSuccess = useCallback(async () => {
    if (!paymentProcessingStartedAtRef.current) {
      openPaymentProcessing();
    }
    await waitForPaymentSuccessLoader();
    paymentProcessingStartedAtRef.current = null;
    closeAlert();
    showContentUnlockedPushToast(purchase_type, creatorInfo);
    setCcBillModalOpen(false);
    resetPurchaseState();
    if (onPurchaseSuccess) {
      onPurchaseSuccess();
    }
    if (toggle) {
      toggle();
    }
  }, [creatorInfo, onPurchaseSuccess, openPaymentProcessing, purchase_type, resetPurchaseState, toggle]);

  const closeCCBillModal = useCallback(async () => {
    const pid = getFormFieldValue(paymentForm, "c_purchase_content_id");
    if (purchaseTransactionId || pid) {
      try {
        await cancelPurchaseByType(purchaseTransactionId || pid, false);
      } catch (_ignored) {
        // ignore cancellation errors when closing modal manually
      }
    }

    setCcBillModalOpen(false);
    resetPurchaseState();
  }, [purchaseTransactionId, resetPurchaseState, paymentForm, cancelPurchaseByType]);

  const cancelCCBillModal = useCallback(async () => {
    const pid = getFormFieldValue(paymentForm, "c_purchase_content_id");
    if (purchaseTransactionId || pid) {
      try {
        await cancelPurchaseByType(purchaseTransactionId || pid, true);
      } catch (_ignored) {
        // ignore cancellation errors when closing modal manually
      }
    }

    setCcBillModalOpen(false);
    resetPurchaseState();
  }, [purchaseTransactionId, resetPurchaseState, paymentForm, cancelPurchaseByType]);

  const handleCCBillModalSuccess = useCallback(async () => {
    await handlePurchaseSuccess();
  }, [handlePurchaseSuccess]);

  const handleCancel = useCallback(
    async (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      closeAlert();
      closeModal();
    },
    [
      ccBillModalOpen,
      cancelPurchase,
      closeAlert,
      closeCCBillModal,
      closeModal,
      purchaseTransactionId,
      showConfirmationModal,
      showPaymentCancelling,
    ]
  );

  const chargeWithExistingToken = useCallback(
    async (purchaseId, chargeData) => {
      try {
        setIsProcessing(true);
        openPaymentProcessing({
          didOpen: () => {
            // Loading state handled by openPaymentProcessing
          },
        });

        const transactionId = purchaseId;
        const res = await charge(chargeData);

        if (res.success) {
          await handlePurchaseSuccess();
          return true;
        } else {
          dismissPaymentProcessing();
          if (transactionId) {
            await cancelPurchaseByType(transactionId);
          }
          showPurchasePaymentError(res.error || "Payment failed. Please try again.");
          return false;
        }
      } catch (error) {
        const transactionId = purchaseId;
        dismissPaymentProcessing();
        if (transactionId) {
          await cancelPurchaseByType(transactionId);
        }
        showPurchasePaymentError(
          error?.response?.data?.message ||
          error?.message ||
          "Payment failed. Please try again."
        );
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [
      cancelPurchaseByType,
      dismissPaymentProcessing,
      handlePurchaseSuccess,
      openPaymentProcessing,
      showPurchasePaymentError,
    ]
  );

  const handleConfirmPurchase = useCallback(async () => {
    if (isProcessing) {
      return false;
    }

    if (!contentId || !purchasePayload) {
      showPurchasePaymentError("Invalid content or price information.");
      return false;
    }

    const creatorId = creatorInfo?._id;
    if (purchase_type === 'message_content' && !creatorId) {
      showPurchasePaymentError("Creator information is required.");
      return false;
    }

    try {
      setIsProcessing(true);
      openPaymentProcessing({
        didOpen: () => {
          // Loading state handled by openPaymentProcessing
        },
      });

      const messagePayload = { ...purchasePayload, content_id: contentId };
      const res =
        purchase_type === 'message_content'
          ? await purchaseMessageContent(creatorId, messagePayload)
          : await purchaseContent(contentId, purchasePayload);
      if (!res.success) {
        dismissPaymentProcessing();
        setShowConfirmationModal(false);
        showPurchasePaymentError(res.error || "Something went wrong. Please try again.");
        return false;
      }

      const responseData = res.data?.data || res.data;
      const {
        purchaseId,
        nextPaymentForm,
        paymentGateway,
        paymentOneClick,
      } = extractPurchaseDetails(responseData);

      const oneClickMeansFullyPaid =
        Number(paymentOneClick) === 1 &&
        (purchase_type !== "message_content" ||
          isPurchaseApiConfirmedComplete(responseData));

      if (oneClickMeansFullyPaid) {
        setShowConfirmationModal(false);
        await handlePurchaseSuccess();
        return null;
      }

      if (paymentGateway !== "CCBILL" || !nextPaymentForm) {
        dismissPaymentProcessing();
        setShowConfirmationModal(false);
        showPurchasePaymentError("Unsupported payment gateway. Please try again.");
        return null;
      }

      if (purchaseId) {
        setPurchaseTransactionId(purchaseId);
      }

      const responseToken = getFormFieldValue(
        nextPaymentForm,
        "paymentTokenId"
      );

      const existingPaymentTokenId =
        responseToken && hasValidValue(responseToken)
          ? responseToken
          : await fetchSavedPaymentToken();

      // if no existing payment token id, show CCBillModal
      if (!existingPaymentTokenId) {
        dismissPaymentProcessing();
        setShowConfirmationModal(false);
        setPaymentForm(nextPaymentForm);
        setCcBillModalOpen(true);
        return null;
      } else {
        const preparedData = {
          paymentTokenId: existingPaymentTokenId,
          clientSubacc:
            getFormFieldValue(nextPaymentForm, "clientSubacc") ||
            process.env.REACT_APP_CCBILL_SUBACCOUNT,
          currencyCode:
            Number(getFormFieldValue(nextPaymentForm, "currencyCode")) ||
            PAYMENT_INFO.currencyCode,
          initialPrice:
            Number(getFormFieldValue(nextPaymentForm, "initialPrice")) ||
            purchasePayload.amount,
          initialPeriod:
            Number(getFormFieldValue(nextPaymentForm, "initialPeriod")) ||
            PAYMENT_INFO.initialPeriod,
          passThroughInfo: buildPassThroughInfo(nextPaymentForm),
        };

        dismissPaymentProcessing();
        const amount = purchasePayload?.amount ?? 0;
        const confirmed = await confirmDialog(
          "Confirm Payment",
          `You have a saved payment method. Would you like to purchase this content for $${Number(amount).toFixed(2)}?`,
          {
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel",
            cancelButtonColor: "#d33",
          }
        );

        if (confirmed) {
          await chargeWithExistingToken(purchaseId, preparedData);
        } else {
          if (purchaseId) {
            try {
              await cancelPurchaseByType(purchaseId, true);
            } catch (_ignored) {
              // ignore cancellation errors
            }
          }
          dismissPaymentProcessing();
        }
        setShowConfirmationModal(false);
        return confirmed;
      }
    } catch (error) {
      dismissPaymentProcessing();
      setShowConfirmationModal(false);
      showPurchasePaymentError(
        error?.response?.data?.message || "Something went wrong. Please try again."
      );
      return false;
    } finally {
      setIsProcessing(false);
      setShowConfirmationModal(false);
    }
  }, [
    chargeWithExistingToken,
    dismissPaymentProcessing,
    contentId,
    creatorInfo,
    cancelPurchase,
    fetchSavedPaymentToken,
    handlePurchaseSuccess,
    openPaymentProcessing,
    purchaseContent,
    purchaseMessageContent,
    purchase_type,
    showPurchasePaymentError,
    isProcessing,
    purchasePayload,
  ]);

  // Keep isOpenRef in sync with isOpen prop
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Handle modal open/close and payment flow initialization
  useEffect(() => {
    if (isOpen) {
      setPurchaseTransactionId(null);
      const openModal = async () => {
        // Don't open modal if CCBill modal is already open
        if (isOpenRef.current && !showConfirmationModal && !ccBillModalOpen) {
          const existingPaymentTokenId = await fetchSavedPaymentToken();
          const hasSavedPayment =
            existingPaymentTokenId && hasValidValue(existingPaymentTokenId);

          setHasSavedPaymentMethod(Boolean(hasSavedPayment));
          setShowConfirmationModal(true);
        }
      };

      openModal();
    } else {
      setShowConfirmationModal(false);
    }

    return undefined;
  }, [fetchSavedPaymentToken, isOpen, showConfirmationModal, ccBillModalOpen]);

  const { username, vatDisplay, totalDisplay } = purchaseSummary;

  const confirmationModalMarkup = showConfirmationModal ? (
    <div
      id="purchase-content-modal"
      className={`subscribe-modal-overlay cm_bg_blur ${showConfirmationModal ? 'subscribe-modal-show' : 'subscribe-modal-hide'}`}
    >
      <div className="subscribe-modal-container">
        <div className="subscribe-modal-content pb_40">
          {/* Header with icon and title */}
          <div className="jh-modern-purchase-header-section">
            <div className="jh-modern-purchase-icon-container">
              <i className="fa-solid fa-lock-open jh-modern-purchase-icon"></i>
            </div>
            <h2 className="jh-modern-purchase-title">
              Confirm Purchase
            </h2>
          </div>

          {/* Creator tag */}
          <div className="jh-modern-purchase-creator-tag">
            @{username}
          </div>

          {/* Instructions */}
          <div className="jh-modern-purchase-instructions">
            Tap home then navigate to Purchased to access all purchased content.
          </div>

          {/* Saved card message */}
          {/* {hasSavedPaymentMethod && (
            <div className="jh-modern-purchase-saved-card-message">
              You have a saved payment method on file. Confirm to complete your purchase instantly.
            </div>
          )} */}

          {/* Total section */}
          <div className="jh-modern-purchase-total-section">
            <div className="jh-modern-purchase-total-box">
              <span className="jh-modern-purchase-total-label">Total</span>
              <span className="jh-modern-purchase-total-amount">${totalDisplay}</span>
            </div>
          </div>


          <div className="jh-modern-purchase-vat-info">
            {/* VAT fee of ${vatDisplay} has been applied. */}
            {getVatAndServiceFeeAppliedText(vatDisplay ?? 0)}
          </div>


          {/* Action buttons */}
          <div className="jh-modern-purchase-actions">
            <button
              onClick={handleConfirmPurchase}
              className="jh-modern-purchase-confirm-btn"
              disabled={isProcessing}
            >
              {/* {isProcessing ? 
                <span>
                  <i className="fa-solid fa-spinner fa-spin jh-modern-purchase-icon-spacing"></i>
                  Processing...
                </span>
              :  */}
              <>
                {hasSavedPaymentMethod && <i className="fa-solid fa-credit-card jh-modern-purchase-icon-spacing"></i>}
                Purchase
              </>
              {/* } */}
            </button>
            <button
              onClick={handleCancel}
              className="jh-modern-purchase-cancel-btn"
              disabled={isProcessing}
            >
              Cancel
            </button>
          </div>

          {/* Security text */}
          <p className="cm_secure_info">
            <i className="fa-solid fa-shield-halved jh-modern-purchase-shield-icon"></i>
            Secure payment powered by CCBill
          </p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Purchase Confirmation Modal */}
      {isBrowser &&
        showConfirmationModal &&
        confirmationModalMarkup &&
        createPortal(confirmationModalMarkup, document.body)
      }

      {/* CCBill Modal */}
      {ccBillModalOpen && paymentForm && (
        <CCBillModal
          paymentForm={paymentForm}
          openModal={ccBillModalOpen}
          toggleModal={closeCCBillModal}
          onCancel={cancelCCBillModal}
          onSuccess={handleCCBillModalSuccess}
          purchaseAmount={purchasePayload?.amount ?? contentPrice}
          creatorInfo={creatorInfo}
          vatAmount={purchasePayload?.vatAmount ?? 0}
          vatPercentage={purchasePayload?.vatPercentage ?? 0}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(PurchaseContentModal);

